import React, { useCallback } from 'react';
import type { FC } from 'react';
import { fields, isType, match, variant } from 'variant';
import type { SelectOption } from '@wix/cli-ui-kit';
import {
  Spinner,
  useAsync,
  SelectOptionAvailability,
  SelectInput,
  Box,
  Link,
  InputLabel,
  ErrorDetails,
} from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { useWixCliAppBi } from '../../bi/index.js';

const RequiredAppOptions = variant({
  Available: fields<{
    options: Array<SelectOption<string>>;
    initialIndex: number;
  }>(),
  FailedToGetRequiredApps: {},
});

interface RequiredApp {
  id: string;
  name: string;
}

interface MultipleAppRequirementProps {
  appId: string;
  requiredApps: RequiredApp[];
  onSubmit: (requiredApps: string[]) => void;
}

export const MultipleAppRequirement: FC<MultipleAppRequirementProps> = ({
  appId,
  requiredApps,
  onSubmit,
}) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { getRequiredApps } = useDevCenterClient();
  const { reportError } = useErrorReporter();

  const handleSubmit = useCallback(
    (option?: SelectOption<string>) => {
      bi.cliFlowStepAnswered({
        question: t('generate_command.required_business_solution'),
        questionKey: 'generate_command.required_business_solution',
        answer: option?.value ?? '',
      });

      onSubmit(option?.value ? [option.value] : []);
    },
    [bi, t, onSubmit]
  );

  const { status } = useAsync(async () => {
    let existingRequiredApps: RequiredApp[];

    try {
      existingRequiredApps = await getRequiredApps(appId);
    } catch (error: unknown) {
      reportError(error);
      handleSubmit();

      return RequiredAppOptions.FailedToGetRequiredApps();
    }

    const options: Array<SelectOption<string>> = requiredApps.map((app) => ({
      title: app.name,
      value: app.id,
      availability: existingRequiredApps.some(({ id }) => id === app.id)
        ? SelectOptionAvailability.Disabled({
            reason: t(
              'generate_command.required_business_solution.already_required'
            ),
          })
        : SelectOptionAvailability.Enabled(),
    }));

    options.push({
      title: t('generate_command.required_business_solution.none'),
      value: '',
    });

    const initialIndex = options.findIndex(
      (_) => !isType(_.availability, SelectOptionAvailability.Disabled)
    );

    return RequiredAppOptions.Available({ options, initialIndex });
  }, [getRequiredApps, appId]);

  return (
    <>
      {match(status, {
        Loading: () => (
          <Spinner
            text={t('generate_command.required_business_solution.loading')}
          />
        ),
        Error: () => null,
        Success: ({ result }) => {
          return match(result, {
            Available: ({ options, initialIndex }) => (
              <SelectInput<string>
                label={t('generate_command.required_business_solution')}
                info={t(
                  'generate_command.required_business_solution.description'
                )}
                options={options}
                initialIndex={initialIndex}
                onSubmit={handleSubmit}
              />
            ),
            FailedToGetRequiredApps: () => (
              <Box marginBottom={1} gap={1} flexDirection="column">
                <InputLabel>
                  {t('generate_command.required_business_solution')}
                </InputLabel>
                <ErrorDetails
                  message={t(
                    'generate_command.required_business_solution.error'
                  )}
                  hint={
                    <Trans
                      i18nKey="generate_command.required_business_solution.error.hint"
                      components={[
                        <Link
                          url={`https://manage.wix.com/apps/${appId}/dev-center-giza-market-listing/app-audience?referralInfo=wix-cli`}
                        />,
                      ]}
                    />
                  }
                />
              </Box>
            ),
          });
        },
      })}
    </>
  );
};
